import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Clock, Flame } from "lucide-react";
import api, { TIER_META } from "../lib/api";

export default function Compose() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/submissions/${id}`).then((r) => setSub(r.data)).catch(() => {
      toast.error("Submission not found"); navigate("/app/queue");
    });
  }, [id, navigate]);

  const submit = async () => {
    if (!text.trim()) return toast.error("Write your roast first.");
    setSubmitting(true);
    try {
      const res = await api.post("/roasts", { submission_id: id, content_text: text });
      if (res.data.status === "rejected_by_mod") toast.error("Moderation blocked this roast — it crosses the guidelines.");
      else if (res.data.moderation_status === "pending_review") toast("Submitted — pending a quick human review.");
      else toast.success("Roast submitted! 🔥 It's in the submitter's inbox.");
      navigate("/app/queue");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  if (!sub) return <div className="text-zinc-500 py-16 text-center">Loading…</div>;
  const meta = TIER_META[sub.tier];

  return (
    <div className="max-w-2xl">
      <button onClick={() => navigate("/app/queue")} className="text-sm text-zinc-500 hover:text-white mb-4">← Back to queue</button>
      <div className="flex items-center gap-3 mb-6">
        <span className="text-[11px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full" style={{ color: meta.color, background: `${meta.color}1a` }}>{meta.label}</span>
        <span className="flex items-center gap-1 text-xs text-zinc-500"><Clock size={13} /> Claimed — submit within 15 min or it's released</span>
      </div>

      <div className="rounded-sm border border-white/10 bg-zinc-900/60 p-5 mb-6">
        <div className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-3">Roast this</div>
        {sub.content_type === "image" ? (
          <img src={sub.content} alt="" className="rounded-sm border border-white/10 max-h-72" />
        ) : (
          <p className="text-lg text-zinc-100 leading-relaxed">"{sub.content}"</p>
        )}
      </div>

      <div className="rounded-sm border border-brand/30 bg-brand/5 p-4 mb-6 flex gap-3">
        <AlertTriangle className="text-brand shrink-0 mt-0.5" size={18} />
        <div className="text-sm text-zinc-300">
          <strong className="text-white">Keep it clean-dirty.</strong> Roast their appearance, taste, career or skills.
          <span className="text-zinc-400"> No jokes about race, religion, caste, disability, gender identity, orientation, or real-world tragedy/self-harm. Only target the consenting submitter.</span>
        </div>
      </div>

      <textarea data-testid="roast-composer" value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={600}
        placeholder="Cook them. Make it land in one read…"
        className="w-full bg-zinc-950 border border-zinc-800 rounded-sm p-4 text-base focus:border-brand focus:outline-none resize-none" />
      <div className="flex items-center justify-between mt-4">
        <span className="text-xs text-zinc-600">{text.length}/600</span>
        <button data-testid="submit-roast-btn" onClick={submit} disabled={submitting}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors disabled:opacity-50">
          {submitting ? "Submitting…" : <>Submit Roast <Flame size={16} /></>}
        </button>
      </div>
    </div>
  );
}
